import React, { useState, useRef, Children, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBusiness } from './BusinessContext';
import { LeadDetailModal, PIPELINE_STAGES, type Lead, type LeadStatus } from './LeadDetailModal';
import { XIcon, SparklesIcon, MessageSquareIcon, TrendingUpIcon, SettingsIcon, FileTextIcon, DollarSignIcon, BotIcon, CheckCircle2Icon, SendIcon, UserIcon, EditIcon, CheckIcon, EyeIcon, ClockIcon, TrashIcon, ArchiveIcon, ToggleRightIcon, UploadCloudIcon, PlusIcon, ImageIcon, MapPinIcon, ExternalLinkIcon, NavigationIcon, SearchIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});
interface CatalogItemModalProps {
  onClose: () => void;
  itemTitle?: string;
}
type Tab = 'Overview' | 'Pricing' | 'AI Knowledge' | 'Leads' | 'Activity' | 'Settings';
export function CatalogItemModal({
  onClose,
  itemTitle = 'Toyota Prado GXR 2018'
}: CatalogItemModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const tabs: {
    id: Tab;
    icon: React.ReactNode;
  }[] = [{
    id: 'Overview',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1116" data-mp="m0c2k8pk9jyx" />
  }, {
    id: 'Pricing',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-1117" data-mp="m2gi5nwto7f1" />
  }, {
    id: 'AI Knowledge',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-1118" data-mp="i138f2qlzk2u" />
  }, {
    id: 'Leads',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-1119" data-mp="36se4fdavq6bj" />
  }, {
    id: 'Activity',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-1120" data-mp="32og8tckrjdn8" />
  }, {
    id: 'Settings',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-1121" data-mp="347g6u0c8as7c" />
  }];
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1122" data-mp="395hnkem6mffn">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-1123" data-mp="3935buj70p1b1" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[85vh]" data-id="element-1124" data-mp="2z7brweqyy5j8">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white z-10" data-id="element-1125" data-mp="2ghvmpe4c9owh">
          <div className="flex items-center gap-4" data-id="element-1126" data-mp="22hum7zrzkjhj">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden" data-id="element-1127" data-mp="26j62vfsvtudl">
              <img src="https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=100&h=100" alt="Prado" className="w-full h-full object-cover" data-id="element-1128" data-mp="1nvdq8z7yvz9o" />
            </div>
            <div data-id="element-1129" data-mp="1wso3ur89c0hr">
              <div className="flex items-center gap-2" data-id="element-1130" data-mp="1rw7clo5rh60g">
                <h2 className="text-xl font-display font-bold text-dark-navy" data-id="element-1131" data-mp="1rumifznczp34">
                  {itemTitle}
                </h2>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full" data-id="element-1132" data-mp="21u5029fsqys0">
                  Active
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500 mt-1" data-id="element-1133" data-mp="2fkqpyi3o48ze">
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-md" data-id="element-1134" data-mp="2bkgggvgo30fs">
                  SUVs
                </span>
                <span data-id="element-1135" data-mp="2kkv5o5wr4zok">•</span>
                <span data-id="element-1136" data-mp="2klo6kq914uys">AED 350/day</span>
                <span data-id="element-1137" data-mp="2kiih39whnr2j">•</span>
                <span className="flex items-center gap-1 text-ai-purple font-medium" data-id="element-1138" data-mp="34n971f0phzha">
                  <SparklesIcon className="w-3 h-3" data-id="element-1139" data-mp="38ob5htmsu7i7" /> AI Ready: 92%
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1140" data-mp="3dgcglc88ds6n">
            <XIcon className="w-5 h-5" data-id="element-1141" data-mp="2lodcg2kxrj2x" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden" data-id="element-1142" data-mp="2lq7wleodv75y">
          {/* Sidebar Tabs */}
          <div className="w-48 flex-shrink-0 border-r border-slate-100 bg-slate-50/50 p-3 overflow-y-auto" data-id="element-1143" data-mp="2prjj3bbync9x">
            <div className="space-y-1" data-id="element-1144" data-mp="2uqwe4jczjpnl">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-primary-indigo shadow-sm border border-slate-200' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-1145" data-mp="2zoxz59tz1z1p">
                  <span className={activeTab === tab.id ? 'text-primary-indigo' : 'text-slate-400'} data-id="element-1146" data-mp="1uh50vmvxllk2">
                    {tab.icon}
                  </span>
                  {tab.id}
                </button>)}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-white p-6" data-id="element-1147" data-mp="1bs85tgspmp0s">
            {activeTab === 'Overview' && <OverviewTab data-id="element-1148" data-mp="16slujcwfzium" />}
            {activeTab === 'Pricing' && <PricingTab data-id="element-1149" data-mp="11szih6sco8ne" />}
            {activeTab === 'AI Knowledge' && <AIRulesTab data-id="element-1150" data-mp="314jueo8v32du" />}
            {activeTab === 'Leads' && <LeadsTab itemTitle={itemTitle} data-id="element-1151" data-mp="314a4f0ntgv85" />}
            {activeTab === 'Activity' && <ActivityTab data-id="element-1152" data-mp="2w6s2hu87vg9n" />}
            {activeTab === 'Settings' && <SettingsTab onClose={onClose} data-id="element-1153" data-mp="ugjhz0848jvl" />}
          </div>
        </div>
      </motion.div>
    </div>;
}
const UAE_PLACES = [{
  id: '1',
  name: 'Al Quoz Industrial Area 3',
  city: 'Dubai',
  area: 'Al Quoz',
  lat: 25.1275,
  lng: 55.2289
}, {
  id: '2',
  name: 'Dubai Marina Mall',
  city: 'Dubai',
  area: 'Dubai Marina',
  lat: 25.0768,
  lng: 55.1404
}, {
  id: '3',
  name: 'Jumeirah Beach Residence',
  city: 'Dubai',
  area: 'JBR',
  lat: 25.0776,
  lng: 55.1328
}, {
  id: '4',
  name: 'Business Bay',
  city: 'Dubai',
  area: 'Business Bay',
  lat: 25.1846,
  lng: 55.2672
}, {
  id: '5',
  name: 'Downtown Dubai',
  city: 'Dubai',
  area: 'Downtown',
  lat: 25.1972,
  lng: 55.2744
}, {
  id: '6',
  name: 'Al Maryah Island',
  city: 'Abu Dhabi',
  area: 'Al Maryah',
  lat: 24.5019,
  lng: 54.3905
}, {
  id: '7',
  name: 'Yas Island',
  city: 'Abu Dhabi',
  area: 'Yas Island',
  lat: 24.4961,
  lng: 54.6039
}, {
  id: '8',
  name: 'Al Majaz Waterfront',
  city: 'Sharjah',
  area: 'Al Majaz',
  lat: 25.3216,
  lng: 55.3855
}];
function MapEvents({
  onLocationSelect
}: {
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}
function OverviewTab() {
  const [isEditing, setIsEditing] = useState(false);
  const [images, setImages] = useState(['https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=1', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=2', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=3']);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [details, setDetails] = useState({
    title: 'Toyota Prado GXR 2023',
    price: '350',
    status: 'Available',
    description: 'Premium SUV perfect for family trips and desert adventures. Fully insured and maintained. Features include leather seats, sunroof, advanced navigation, and 4WD capabilities.',
    specs: {
      Category: 'SUVs',
      Make: 'Toyota',
      Model: 'Prado GXR',
      Year: '2023',
      Seats: '7',
      Transmission: 'Automatic',
      'Fuel Type': 'Petrol'
    }
  });
  const [location, setLocation] = useState({
    address: 'Al Quoz Industrial Area 3, Dubai, UAE',
    city: 'Dubai',
    area: 'Al Quoz',
    lat: 25.1275,
    lng: 55.2289
  });
  // Edit mode location state
  const [draftLocation, setDraftLocation] = useState(location);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filteredPlaces = UAE_PLACES.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.city.toLowerCase().includes(searchQuery.toLowerCase()) || p.area.toLowerCase().includes(searchQuery.toLowerCase()));
  const handlePlaceSelect = (place: (typeof UAE_PLACES)[0]) => {
    setDraftLocation({
      address: `${place.name}, ${place.city}, UAE`,
      city: place.city,
      area: place.area,
      lat: place.lat,
      lng: place.lng
    });
    setSearchQuery(place.name);
    setShowSuggestions(false);
  };
  const handleMapClick = (lat: number, lng: number) => {
    // Resolve the dropped pin to the nearest known UAE place so we can capture
    // a real city + area (lightweight reverse-geocode against the curated set).
    const nearest = UAE_PLACES.reduce((closest, place) => {
      const dist = (place.lat - lat) ** 2 + (place.lng - lng) ** 2;
      const closestDist = (closest.lat - lat) ** 2 + (closest.lng - lng) ** 2;
      return dist < closestDist ? place : closest;
    }, UAE_PLACES[0]);
    setDraftLocation({
      address: `Near ${nearest.area}, ${nearest.city}, UAE`,
      city: nearest.city,
      area: nearest.area,
      lat,
      lng
    });
    setSearchQuery('');
  };
  const saveLocation = () => {
    setLocation(draftLocation);
    // Optional: show a toast here
  };
  const mapQuery = location.address || `${location.area} ${location.city}`;
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const newImages = [...images, images[0]];
      setImages(newImages);
      setActiveImageIndex(newImages.length - 1);
    }
  };
  const handleDeleteImage = () => {
    if (images.length > 1) {
      const newImages = images.filter((_, idx) => idx !== activeImageIndex);
      setImages(newImages);
      setActiveImageIndex(Math.max(0, activeImageIndex - 1));
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <div className="flex flex-col gap-8 pb-2" data-id="element-1154" data-mp="2v0jbjoko2ynm">
      {/* ===== HERO: Title + Grand Gallery ===== */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col gap-5" data-id="element-1155" data-mp="2z24thoc2634o">
        {/* Title row */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1156" data-mp="2q0y9v6e44c77">
          <div data-id="element-1157" data-mp="2g9aptqjvxxfr">
            <div className="flex items-center gap-3 mb-2.5" data-id="element-1158" data-mp="2g8sle6n2kcwr">
              <span className="px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm" data-id="element-1159" data-mp="2g77oiada8hvq">
                {details.status}
              </span>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1160" data-mp="csnnrkhofj5a">
                {details.specs.Category}
              </span>
              <span className="text-xs font-medium text-slate-400 flex items-center gap-1" data-id="element-1161" data-mp="80mdu57ze4if">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-1162" data-mp="3341e4ur975o" /> Updated 2h ago
              </span>
            </div>
            <h3 className="text-3xl sm:text-4xl font-display font-bold text-dark-navy leading-tight" data-id="element-1163" data-mp="3v8hodnhpberl">
              {details.title}
            </h3>
          </div>
          <div className="flex items-center gap-4 shrink-0" data-id="element-1164" data-mp="qthwk48mzm4h">
            <div className="flex items-baseline gap-1" data-id="element-1165" data-mp="mqtk7mkprx2r">
              <span className="text-3xl font-bold text-primary-indigo tracking-tight" data-id="element-1166" data-mp="mpa3xvz25m0z">
                <span className="text-lg text-primary-indigo/70 font-semibold mr-1" data-id="element-1167" data-mp="hqyuhmcoddja">
                  AED
                </span>
                {details.price}
              </span>
              <span className="text-sm font-medium text-slate-500" data-id="element-1168" data-mp="32hpvayvxn68h">/day</span>
            </div>
            {!isEditing && <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 shadow-sm rounded-xl hover:border-primary-indigo hover:text-primary-indigo hover:shadow transition-all group" data-id="element-1169" data-mp="1i1i2jrfi5bc1">
                <EditIcon className="w-4 h-4 text-slate-400 group-hover:text-primary-indigo transition-colors" data-id="element-1170" data-mp="1vt6f1vtny1tj" />
                Edit
              </button>}
          </div>
        </motion.div>

        {/* Grand gallery: large main image + thumbnail rail */}
        <motion.div variants={itemVariants} className="flex flex-col gap-3" data-id="element-1171" data-mp="2ap43st19c3vq">
          <div className="aspect-[16/9] w-full max-w-xl max-h-[300px] bg-slate-50 rounded-2xl overflow-hidden relative group shadow-sm border border-slate-200/60" data-id="element-1172" data-mp="2ep69dda45hga">
            <AnimatePresence mode="wait" data-id="element-1173" data-mp="kbwv786mh7rp">
              <motion.img key={activeImageIndex} src={images[activeImageIndex]} alt="Main" className="w-full h-full object-cover" initial={{
              opacity: 0,
              scale: 1.02
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-1174" data-mp="kcy8ljb426cl" />
            </AnimatePresence>
            <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-[2px]" data-id="element-1175" data-mp="2xjedqu2omb9d">
              <button className="px-4 py-2 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 shadow-sm flex items-center gap-2 transition-colors" onClick={() => fileInputRef.current?.click()} data-id="element-1176" data-mp="37fpupxd7vpa5">
                <UploadCloudIcon className="w-4 h-4" data-id="element-1177" data-mp="u99nb81gls3v" /> Replace
              </button>
              {images.length > 1 && <button className="p-2 bg-white text-red-600 rounded-lg hover:bg-red-50 shadow-sm transition-colors" onClick={handleDeleteImage} title="Delete Photo" data-id="element-1178" data-mp="z0j7vu85i58w">
                  <TrashIcon className="w-4 h-4" data-id="element-1179" data-mp="paq2xc2ikodm" />
                </button>}
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar" data-id="element-1180" data-mp="1cxy5vncgzlch">
            {images.map((img, idx) => <button key={idx} onClick={() => setActiveImageIndex(idx)} className={`w-24 h-20 rounded-xl flex-shrink-0 overflow-hidden border-2 transition-all ${idx === activeImageIndex ? 'border-primary-indigo shadow-sm ring-2 ring-primary-indigo/20 ring-offset-1' : 'border-transparent hover:border-slate-300 opacity-70 hover:opacity-100'}`} data-id="element-1181" data-mp="13z2w61a7vt4f" data-mpi={idx}>
                <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" data-id="element-1182" data-mp="1z8pqz26sysf3" />
              </button>)}
            <button className="w-24 h-20 rounded-xl flex-shrink-0 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-primary-indigo hover:text-primary-indigo hover:bg-indigo-50/50 transition-colors bg-slate-50" onClick={() => fileInputRef.current?.click()} data-id="element-1183" data-mp="1phv3xvlqfosl">
              <PlusIcon className="w-5 h-5 mb-0.5" data-id="element-1184" data-mp="1bkonw8se8472" />
              <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1185" data-mp="16iqyewtegd5o">
                Add
              </span>
            </button>
          </div>
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} data-id="element-1186" data-mp="12ka092ryid0e" />
        </motion.div>
      </motion.div>

      {/* ===== FULL-WIDTH DETAILS ===== */}
      {isEditing ? <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex flex-col" data-id="element-1187" data-mp="im2z1ddnd1l4">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100" data-id="element-1188" data-mp="wfki6x2f8cam">
            <h3 className="text-xl font-bold text-dark-navy flex items-center gap-2" data-id="element-1189" data-mp="3vupbprpdsd2n">
              <EditIcon className="w-5 h-5 text-primary-indigo" data-id="element-1190" data-mp="3vx20anpn9loo" />
              Edit Details
            </h3>
            <div className="flex items-center gap-2" data-id="element-1191" data-mp="8pzn5fsztokz">
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-1192" data-mp="8p7zqs9jux5s">
                Cancel
              </button>
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-all flex items-center gap-2" data-id="element-1193" data-mp="1p2y8wdfj0bma">
                <CheckIcon className="w-4 h-4" data-id="element-1194" data-mp="vqru3f2w8k9q" /> Save Changes
              </button>
            </div>
          </div>

          <div className="space-y-6" data-id="element-1195" data-mp="14tj55druxe0u">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1196" data-mp="14shjygh4nb5e">
              <div className="sm:col-span-2" data-id="element-1197" data-mp="1jhnomjyqhtko">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1198" data-mp="1jgcdfz2yljjj">
                  Title
                </label>
                <input type="text" value={details.title} onChange={e => setDetails({
              ...details,
              title: e.target.value
            })} className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1199" data-mp="1sill8h6ueyt8" />
              </div>
              <div data-id="element-1200" data-mp="3k58h6gezp2bk">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1201" data-mp="3k5s2m73um8uc">
                  Price (AED/day)
                </label>
                <div className="relative" data-id="element-1202" data-mp="3bdq3qd9fh0m6">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium" data-id="element-1203" data-mp="3bcoqq38uq41t">
                    AED
                  </span>
                  <input type="text" value={details.price} onChange={e => setDetails({
                ...details,
                price: e.target.value
              })} className="w-full pl-12 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1204" data-mp="31fk2zyzcy0iw" />
                </div>
              </div>
              <div data-id="element-1205" data-mp="1u63z5cb53th1">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1206" data-mp="1yydlkid1qbek">
                  Status
                </label>
                <select value={details.status} onChange={e => setDetails({
              ...details,
              status: e.target.value
            })} className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all appearance-none" data-id="element-1207" data-mp="1kb29mfcycpdx">
                  <option value="Available" data-id="element-1208" data-mp="27y0lezq4n95i">Available</option>
                  <option value="Rented" data-id="element-1209" data-mp="sitdxdoys12e">Rented</option>
                  <option value="Maintenance" data-id="element-1210" data-mp="sh9zlsmvc039">Maintenance</option>
                </select>
              </div>
              <div className="sm:col-span-2" data-id="element-1211" data-mp="wi29mo3ke90l">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1212" data-mp="ozundxpcmn9m">
                  Description
                </label>
                <textarea rows={4} value={details.description} onChange={e => setDetails({
              ...details,
              description: e.target.value
            })} className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none resize-none transition-all leading-relaxed" data-id="element-1213" data-mp="k1azhrr731em" />
              </div>
            </div>

            <div data-id="element-1214" data-mp="b7o78bm74ga6">
              <div className="flex items-center gap-2 mb-4" data-id="element-1215" data-mp="b760gl1wqjo0">
                <div className="h-px flex-1 bg-slate-100" data-id="element-1216" data-mp="3jo0v5d8jwxr0" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-1217" data-mp="3epzawoze2sdy">
                  Key Specifications
                </span>
                <div className="h-px flex-1 bg-slate-100" data-id="element-1218" data-mp="887jk1z0qu1m" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1219" data-mp="c998npz81ppv">
                {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-1220" data-mp="h3djwlfisjwr">
                    <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-1221" data-mp="3lgtsc57mtldv">
                      {key}
                    </label>
                    <input type="text" value={val} onChange={e => setDetails({
                ...details,
                specs: {
                  ...details.specs,
                  [key]: e.target.value
                }
              })} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm transition-all" data-id="element-1222" data-mp="3ufxfpldu0my6" />
                  </div>)}
              </div>
            </div>

            {/* Interactive Product Location Editor */}
            <div data-id="element-1223" data-mp="2ggca3wm2dgg6">
              <div className="flex items-center gap-2 mb-4" data-id="element-1224" data-mp="2ggvmyywuxenc">
                <div className="h-px flex-1 bg-slate-100" data-id="element-1225" data-mp="2gdzmp3xjedvq" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-1226" data-mp="2bgg9868ny54u">
                  Product Location
                </span>
                <div className="h-px flex-1 bg-slate-100" data-id="element-1227" data-mp="21j3888z4wua7" />
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-id="element-1228" data-mp="21l68rmxwy5t6">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-1229" data-mp="4ofgt5hodyjb">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1230" data-mp="3vxvzxwwwd0wv">
                    Search Location
                  </label>
                  <div className="relative" data-id="element-1231" data-mp="cs0wyxua7510">
                    <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1232" data-mp="cqzc61ngr463" />
                    <input value={searchQuery} onChange={e => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }} onFocus={() => setShowSuggestions(true)} placeholder="Search places in UAE..." className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all text-sm" data-id="element-1233" data-mp="3tzcpdbej0a5c" />
                    {showSuggestions && searchQuery && filteredPlaces.length > 0 && <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-id="element-1234" data-mp="2nlq6iw3sroq5">
                          {filteredPlaces.map(place => <button key={place.id} onClick={() => handlePlaceSelect(place)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors flex items-center gap-3" data-id="element-1235" data-mp="2wm67nyx2ijru">
                              <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-1236" data-mp="31l8zgho1ethv" />
                              <div data-id="element-1237" data-mp="3fbmaisw86obf">
                                <div className="text-sm font-medium text-slate-900" data-id="element-1238" data-mp="3feqim1cta47d">
                                  {place.name}
                                </div>
                                <div className="text-xs text-slate-500" data-id="element-1239" data-mp="3pbljgqwk4got">
                                  {place.area}, {place.city}
                                </div>
                              </div>
                            </button>)}
                        </div>}
                  </div>
                </div>

                <div className="h-64 relative z-10" data-id="element-1240" data-mp="2qz0poin6yyye">
                  <MapContainer center={[draftLocation.lat, draftLocation.lng]} zoom={13} scrollWheelZoom={false} className="w-full h-full" data-id="element-1241" data-mp="2qxfwoxgizo2n">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1242" data-mp="35ndr3v51ta0a" />
                    <Marker position={[draftLocation.lat, draftLocation.lng]} data-id="element-1243" data-mp="3eo22sj6yyya0" />
                    <MapEvents onLocationSelect={handleMapClick} data-id="element-1244" data-mp="1fd0y9oihfajc" />
                  </MapContainer>
                </div>

                <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1245" data-mp="1kb2gk78335tr">
                  <div data-id="element-1246" data-mp="1802sxwxirz80">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-1247" data-mp="17zt2y9ch5s2b">
                      {draftLocation.address}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-1248" data-mp="12z5h0233s1ey">
                      Coordinates: {draftLocation.lat.toFixed(4)},{' '}
                      {draftLocation.lng.toFixed(4)}
                    </div>
                  </div>
                  <button onClick={saveLocation} className="px-4 py-2 bg-primary-indigo text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2 shrink-0 shadow-sm" data-id="element-1249" data-mp="1qs41xs40d5v5">
                    <CheckIcon className="w-4 h-4" data-id="element-1250" data-mp="ackq12qjhrbx" /> Save Location
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col gap-8" data-id="element-1251" data-mp="25g8uqcjd8jzk">
          {/* AI Readiness Card (full width) */}
          <motion.div variants={itemVariants} data-id="element-1252" data-mp="1wfucxn5m1n0t">
            <div className="relative overflow-hidden bg-purple-50 border border-purple-100 rounded-2xl p-5 shadow-sm" data-id="element-1253" data-mp="1wh5fxkr8eqqv">
              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1254" data-mp="21g8got06iat1">
                <div className="flex items-center gap-3" data-id="element-1255" data-mp="2t5jze53u6yiz">
                  <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0" data-id="element-1256" data-mp="2t51yuwa55y59">
                    <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-1257" data-mp="2fc2heoqsttv2" />
                  </div>
                  <div data-id="element-1258" data-mp="2kb5bxf95khos">
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-1259" data-mp="382ubngob08fr">
                      AI Readiness Score
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-1260" data-mp="3cum1tq3jsc5d">
                      Optimized for AI matching
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-1261" data-mp="3i7aqc6q1rjhz">
                  <div className="w-40 h-2.5 bg-white rounded-full overflow-hidden shadow-inner" data-id="element-1262" data-mp="3i4oi04vc0zyp">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: '92%'
                }} transition={{
                  duration: 1,
                  delay: 0.2,
                  ease: 'easeOut'
                }} className="h-full bg-ai-purple rounded-full" data-id="element-1263" data-mp="3w0a0ruaz2nio" />
                  </div>
                  <span className="font-bold text-ai-purple text-lg" data-id="element-1264" data-mp="38axv34eusxl4">92%</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Description (full width) */}
          <motion.div variants={itemVariants} data-id="element-1265" data-mp="nkqh9n03sjgt">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1266" data-mp="2ck3ujymckzyo">
              <FileTextIcon className="w-4 h-4 text-slate-400" data-id="element-1267" data-mp="27iwp467ogcv6" /> Description
            </h4>
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5" data-id="element-1268" data-mp="23hkxjv58thkc">
              <p className="text-sm text-slate-600 leading-relaxed max-w-3xl" data-id="element-1269" data-mp="1yjt5n14llvg5">
                {details.description}
              </p>
            </div>
          </motion.div>

          {/* Key Specs Grid (full width) */}
          <motion.div variants={itemVariants} data-id="element-1270" data-mp="2r9027ukhsayr">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1271" data-mp="2rab6rwlrhmqx">
              <SettingsIcon className="w-4 h-4 text-slate-400" data-id="element-1272" data-mp="2mf466xnsgy68" /> Key
              Specifications
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-1273" data-mp="3exjawb7geprd">
              {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-slate-300 transition-all group" data-id="element-1274" data-mp="3a61b3qh62v7z">
                  <span className="block text-xs font-medium text-slate-500 mb-1 group-hover:text-primary-indigo transition-colors" data-id="element-1275" data-mp="3mhaq9ssfch1j">
                    {key}
                  </span>
                  <span className="block font-semibold text-slate-900 text-sm truncate" title={val} data-id="element-1276" data-mp="3viiaj6y3vhcm">
                    {val}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Product Location (full width) */}
          <motion.div variants={itemVariants} data-id="element-1277" data-mp="e3laxo4lxqh2">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1278" data-mp="izacty43njgj">
              <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-1279" data-mp="n2ovkwjfadxo" /> Location
            </h4>
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5 flex flex-col gap-4" data-id="element-1280" data-mp="s0gz6ntfjohp">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1281" data-mp="2o3dam2p4ejcb">
                <div className="flex items-start gap-3" data-id="element-1282" data-mp="2k332okhrpiut">
                  <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0" data-id="element-1283" data-mp="2k0gpa487494o">
                    <MapPinIcon className="w-5 h-5 text-primary-indigo" data-id="element-1284" data-mp="2t2zmo8t7ptjj" />
                  </div>
                  <div data-id="element-1285" data-mp="37rcm5friduis">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-1286" data-mp="37qt0pp2ngo00">
                      {location.area}, {location.city}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-md" data-id="element-1287" data-mp="3gsu1ux107u6y">
                      {location.address}
                    </p>
                    <div className="flex items-center gap-2 mt-2" data-id="element-1288" data-mp="3bv0ui0nysu53">
                      <span className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1289" data-mp="1mejdepobn21p">
                        City: {location.city}
                      </span>
                      <span className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1290" data-mp="3t2jooadbzeid">
                        Area: {location.area}
                      </span>
                    </div>
                  </div>
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`} target="_blank" rel="noreferrer" className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:border-primary-indigo hover:text-primary-indigo transition-colors flex items-center justify-center gap-2 whitespace-nowrap shrink-0" data-id="element-1291" data-mp="3ocbl09ac6i2z">
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-1292" data-mp="35dqvglo3fkrg" /> Open in Maps
                </a>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm h-56 relative z-10" data-id="element-1293" data-mp="ziekk7yh1wga">
                <MapContainer center={[location.lat, location.lng]} zoom={14} scrollWheelZoom={false} className="w-full h-full" data-id="element-1294" data-mp="evg9azj9ox4f">
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1295" data-mp="4wr1y0ozyv2f" />
                  <Marker position={[location.lat, location.lng]} data-id="element-1296" data-mp="3s2rp7xzz414c" />
                </MapContainer>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </div>;
}
interface DeliveryRow {
  id: number;
  location: string;
  charge: string;
}
interface AddonRow {
  id: number;
  item: string;
  type: string;
  amount: string;
}
function PricingTab() {
  const [isEditing, setIsEditing] = useState(false);
  const [base, setBase] = useState({
    daily: '350',
    weekly: '2100',
    monthly: '7500'
  });
  const [delivery, setDelivery] = useState<DeliveryRow[]>([{
    id: 1,
    location: 'Inside Dubai',
    charge: '50'
  }, {
    id: 2,
    location: 'Sharjah',
    charge: '100'
  }, {
    id: 3,
    location: 'Airport Delivery',
    charge: '75'
  }]);
  const [addons, setAddons] = useState<AddonRow[]>([{
    id: 1,
    item: 'Security Deposit',
    type: 'Refundable',
    amount: '1000'
  }, {
    id: 2,
    item: 'Additional Driver',
    type: 'Per Day',
    amount: '25'
  }, {
    id: 3,
    item: 'Child Seat',
    type: 'Fixed',
    amount: '50'
  }]);
  const num = (v: string) => parseFloat(v.replace(/,/g, '')) || 0;
  const fmt = (v: number) => v.toLocaleString('en-US');
  // Auto-computed savings vs. paying the daily rate for the whole period.
  const weeklyDiscount = Math.max(0, Math.round((1 - num(base.weekly) / (num(base.daily) * 7)) * 100));
  const monthlyDiscount = Math.max(0, Math.round((1 - num(base.monthly) / (num(base.daily) * 30)) * 100));
  const updateDelivery = (id: number, field: keyof DeliveryRow, val: string) => setDelivery(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addDelivery = () => setDelivery(prev => [...prev, {
    id: Date.now(),
    location: 'New location',
    charge: '0'
  }]);
  const removeDelivery = (id: number) => setDelivery(prev => prev.filter(r => r.id !== id));
  const updateAddon = (id: number, field: keyof AddonRow, val: string) => setAddons(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addAddon = () => setAddons(prev => [...prev, {
    id: Date.now(),
    item: 'New add-on',
    type: 'Fixed',
    amount: '0'
  }]);
  const removeAddon = (id: number) => setAddons(prev => prev.filter(r => r.id !== id));
  const baseCards = [{
    key: 'daily' as const,
    label: 'Daily Rate',
    discount: 0
  }, {
    key: 'weekly' as const,
    label: 'Weekly Rate',
    discount: weeklyDiscount
  }, {
    key: 'monthly' as const,
    label: 'Monthly Rate',
    discount: monthlyDiscount
  }];
  return <div className="max-w-3xl space-y-8" data-id="element-1297" data-mp="3dg2usakkwe4t">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1298" data-mp="3hh4lu44cdjvp">
        <div data-id="element-1299" data-mp="3hipdnlz9uopv">
          <h3 className="text-xl font-bold text-dark-navy flex items-center gap-2" data-id="element-1300" data-mp="33pq03pj1v4ky">
            <DollarSignIcon className="w-5 h-5 text-primary-indigo" data-id="element-1301" data-mp="33nna35hwdbg8" /> Pricing
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1302" data-mp="38m81qgj32mt2">
            Set the rates, charges and add-ons the AI quotes to customers.
          </p>
        </div>
        {isEditing ? <div className="flex items-center gap-2" data-id="element-1303" data-mp="50sjqcypnykw">
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-1304" data-mp="2134exlkhn7r1">
              Cancel
            </button>
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2" data-id="element-1305" data-mp="1mdq40919ki9j">
              <CheckIcon className="w-4 h-4" data-id="element-1306" data-mp="2f3f1wdiog26x" /> Save Changes
            </button>
          </div> : <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 shadow-sm rounded-xl hover:border-primary-indigo hover:text-primary-indigo hover:shadow transition-all group" data-id="element-1307" data-mp="2a1zmb0z9qnvx">
            <EditIcon className="w-4 h-4 text-slate-400 group-hover:text-primary-indigo transition-colors" data-id="element-1308" data-mp="ul8u9c5f2il8" />
            Edit Pricing
          </button>}
      </div>

      {/* Base Pricing */}
      <section data-id="element-1309" data-mp="3abqwfol10bv8">
        <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-1310" data-mp="3a94k7bn6x86o">Base Pricing</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1311" data-mp="3f995rg2ygt3r">
          {baseCards.map(card => <div key={card.key} className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm" data-id="element-1312" data-mp="3k3dcpzc8114u">
              <div className="flex items-center justify-between mb-2" data-id="element-1313" data-mp="3tyyhu6cl6hit">
                <div className="text-sm text-slate-500" data-id="element-1314" data-mp="1vnxq8bw5xj1">{card.label}</div>
                {card.discount > 0 && <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-md" data-id="element-1315" data-mp="6uigxbdrto3o">
                    Save {card.discount}%
                  </span>}
              </div>
              {isEditing ? <div className="relative" data-id="element-1316" data-mp="28o7x5uqzr83t">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium" data-id="element-1317" data-mp="2kcj2lfu01fnj">
                    AED
                  </span>
                  <input type="text" value={base[card.key]} onChange={e => setBase({
              ...base,
              [card.key]: e.target.value
            })} className="w-full pl-12 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-lg font-bold text-dark-navy focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1318" data-mp="2pcxbf03fctmm" />
                </div> : <div className="text-xl font-bold text-dark-navy" data-id="element-1319" data-mp="1iv1t2xbtsgjz">
                  AED {fmt(num(base[card.key]))}
                </div>}
            </div>)}
        </div>
        {isEditing && <p className="text-xs text-slate-400 mt-2" data-id="element-1320" data-mp="4zimec9iehl6">
            Weekly and monthly savings are calculated automatically against the
            daily rate.
          </p>}
      </section>

      {/* Delivery & Pickup */}
      <section data-id="element-1321" data-mp="3s2ez8ugffvnz">
        <div className="flex items-center justify-between mb-3" data-id="element-1322" data-mp="3s1vgxcmv759f">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1323" data-mp="3n5ewasevj913">
            Delivery &amp; Pickup Charges
          </h4>
          {isEditing && <button onClick={addDelivery} className="text-xs font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-1324" data-mp="3i7n3zxabhkwd">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1325" data-mp="39b48cdapbiw3" /> Add location
            </button>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1326" data-mp="34cax6riw03z3">
          <table className="w-full text-left text-sm" data-id="element-1327" data-mp="2zh5avrlkonah">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1328" data-mp="2ugzgi3nx4mop">
              <tr data-id="element-1329" data-mp="2kslssi3iuu36">
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1330" data-mp="2ks2k7rzrhi1t">
                  Location / Service
                </th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1331" data-mp="2pt02s5oooe3u">Charge</th>
                {isEditing && <th className="px-4 py-3 w-10" data-id="element-1332" data-mp="2bsyyshd4gqk5" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1333" data-mp="2fuahs41hqdjd">
              {delivery.map(row => <tr key={row.id} data-id="element-1334" data-mp="33jv3uvsxxqwv">
                  <td className="px-4 py-2.5 text-dark-navy" data-id="element-1335" data-mp="33kwqlxjbk1uf">
                    {isEditing ? <input type="text" value={row.location} onChange={e => updateDelivery(row.id, 'location', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1336" data-mp="38k0xghirtnhl" /> : row.location}
                  </td>
                  <td className="px-4 py-2.5 font-medium" data-id="element-1337" data-mp="1ashza5lorizb">
                    {isEditing ? <div className="relative w-32" data-id="element-1338" data-mp="1pl31vz4u4tss">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1339" data-mp="1kl6xvzw512d8">
                          AED
                        </span>
                        <input type="text" value={row.charge} onChange={e => updateDelivery(row.id, 'charge', e.target.value)} className="w-full pl-10 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1340" data-mp="rvq8lb3te1l8" />
                      </div> : `AED ${fmt(num(row.charge))}`}
                  </td>
                  {isEditing && <td className="px-4 py-2.5 text-right" data-id="element-1341" data-mp="vs4d56ndfs2s">
                      <button onClick={() => removeDelivery(row.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Remove" data-id="element-1342" data-mp="vuqj51um5ziw">
                        <TrashIcon className="w-4 h-4" data-id="element-1343" data-mp="1anttop9sfd04" />
                      </button>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Add-ons & Deposits */}
      <section data-id="element-1344" data-mp="1ohul0xyetxqg">
        <div className="flex items-center justify-between mb-3" data-id="element-1345" data-mp="1oidyo2h11zyo">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1346" data-mp="1tfmn2ksrr08d">
            Add-ons &amp; Deposits
          </h4>
          {isEditing && <button onClick={addAddon} className="text-xs font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-1347" data-mp="23lnviy905q7e">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1348" data-mp="1tmf349tscjn5" /> Add item
            </button>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1349" data-mp="1plwcuk7jjln2">
          <table className="w-full text-left text-sm" data-id="element-1350" data-mp="2m99cobp2dylu">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1351" data-mp="2hbrbj7hagnoe">
              <tr data-id="element-1352" data-mp="2hdkiknvtd9wx">
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1353" data-mp="2ckre8149c3ld">Item</th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1354" data-mp="2ciocwkxnmo1c">Type</th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1355" data-mp="28io014q7ekwe">Amount</th>
                {isEditing && <th className="px-4 py-3 w-10" data-id="element-1356" data-mp="351vbn4v9q5an" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1357" data-mp="309alcdbkmabv">
              {addons.map(row => <tr key={row.id} data-id="element-1358" data-mp="yduqmsggiegs">
                  <td className="px-4 py-2.5 text-dark-navy" data-id="element-1359" data-mp="yewddu6u4pec">
                    {isEditing ? <input type="text" value={row.item} onChange={e => updateAddon(row.id, 'item', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1360" data-mp="13dz74ihd7970" /> : row.item}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500" data-id="element-1361" data-mp="anh6rp80zlxa">
                    {isEditing ? <select value={row.type} onChange={e => updateAddon(row.id, 'type', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all appearance-none" data-id="element-1362" data-mp="202yezwe1e8yo">
                        <option value="Refundable" data-id="element-1363" data-mp="3uhlt0q6tciwp">Refundable</option>
                        <option value="Per Day" data-id="element-1364" data-mp="c3uzkk02ox2c">Per Day</option>
                        <option value="Fixed" data-id="element-1365" data-mp="c6h4ebvkwygv">Fixed</option>
                      </select> : row.type}
                  </td>
                  <td className="px-4 py-2.5 font-medium" data-id="element-1366" data-mp="m1sl2zqjsfr9">
                    {isEditing ? <div className="relative w-32" data-id="element-1367" data-mp="h4a9f1l5bmfk">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1368" data-mp="2nt5fuvysx79l">
                          AED
                        </span>
                        <input type="text" value={row.amount} onChange={e => updateAddon(row.id, 'amount', e.target.value)} className="w-full pl-10 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1369" data-mp="1x1ofru3a0ak0" />
                      </div> : `AED ${fmt(num(row.amount))}`}
                  </td>
                  {isEditing && <td className="px-4 py-2.5 text-right" data-id="element-1370" data-mp="1sztkb6810re5">
                      <button onClick={() => removeAddon(row.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Remove" data-id="element-1371" data-mp="1t1e9shfhhk55">
                        <TrashIcon className="w-4 h-4" data-id="element-1372" data-mp="sqlfmzuqfzbz" />
                      </button>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
// Status pill colors, aligned with the leads pipeline used in the Leads section.
function leadStatusBadge(status: LeadStatus) {
  switch (status) {
    case 'Won':
    case 'Ready To Buy':
      return 'bg-green-100 text-green-700';
    case 'Qualified':
      return 'bg-indigo-100 text-primary-indigo';
    case 'Negotiating':
      return 'bg-blue-100 text-blue-700';
    case 'Interested':
      return 'bg-amber-100 text-amber-700';
    case 'Lost':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}
function LeadsTab({
  itemTitle
}: {
  itemTitle: string;
}) {
  // Leads MAPPED to this specific item (matchedItem === itemTitle), using the
  // same Lead shape + pipeline stages as the main Leads section so they stay
  // consistent and open the same detail view.
  const [leads, setLeads] = useState<Lead[]>([{
    name: 'Ahmed Al Mansoori',
    intentScore: 94,
    lookingFor: 'Toyota Prado',
    budget: 'AED 90,000',
    timeline: 'Within 7 Days',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Qualified',
    summary: 'Customer strongly prefers GCC vehicles. Ready to purchase this week.',
    phone: '+971 50 123 4567',
    email: 'ahmed@email.com',
    preferredContact: 'WhatsApp',
    preferredLanguage: 'Arabic',
    source: 'Talab AI',
    lastActive: '12 minutes ago',
    receivedDate: 'Jun 6, 2026',
    owner: 'Sara',
    nextAction: 'Call customer',
    nextActionDue: 'Today',
    consent: 'shared'
  }, {
    name: 'Omar Haddad',
    intentScore: 67,
    lookingFor: 'Family SUV',
    budget: 'AED 70,000 - 85,000',
    timeline: 'This Month',
    location: 'Sharjah',
    matchedItem: itemTitle,
    status: 'Interested',
    summary: 'Exploring options for a family vehicle. Requested availability confirmation.',
    source: 'Talab AI',
    lastActive: '3 hours ago',
    receivedDate: 'Jun 5, 2026',
    owner: 'Unassigned',
    nextAction: 'Follow up tomorrow',
    nextActionDue: 'Tomorrow',
    consent: 'requested'
  }, {
    name: 'Layla Hassan',
    intentScore: 88,
    lookingFor: 'Toyota Prado GXR',
    budget: 'AED 95,000',
    timeline: 'Within 2 Weeks',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Negotiating',
    summary: 'Asked about delivery to Dubai Marina and the security deposit. Strong engagement.',
    phone: '+971 52 444 1212',
    email: 'layla.h@email.com',
    preferredContact: 'Phone Call',
    preferredLanguage: 'English',
    source: 'Talab AI',
    lastActive: '1 hour ago',
    receivedDate: 'Jun 6, 2026',
    owner: 'Fadi',
    nextAction: 'Send quotation',
    nextActionDue: 'Today',
    consent: 'shared'
  }, {
    name: 'Anonymous Buyer',
    intentScore: 41,
    lookingFor: 'Compact SUV',
    budget: 'AED 60,000',
    timeline: 'Undecided',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Discovered',
    summary: 'Early-stage research. Asked a single pricing question.',
    source: 'Talab AI',
    lastActive: '2 days ago',
    receivedDate: 'Jun 4, 2026',
    owner: 'Unassigned',
    consent: 'not_shared'
  }]);
  const [statusFilter, setStatusFilter] = useState<'All' | LeadStatus>('All');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const filtered = statusFilter === 'All' ? leads : leads.filter(l => l.status === statusFilter);
  const updateStatus = (status: LeadStatus) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      status
    } : l));
    return {
      ...cur,
      status
    };
  });
  const updateOwner = (owner: string) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      owner
    } : l));
    return {
      ...cur,
      owner
    };
  });
  return <div data-id="element-1373" data-mp="1w5edc2nigkkz">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5" data-id="element-1374" data-mp="1w321864fp4fu">
        <div data-id="element-1375" data-mp="204n6p6en2gg0">
          <h3 className="text-lg font-bold text-dark-navy" data-id="element-1376" data-mp="204x259igdft3">Leads</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-1377" data-mp="24z1a9w3gftvr">
            {leads.length} buyers matched to this item.
          </p>
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'All' | LeadStatus)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1378" data-mp="1ch5kdqe58akf">
          <option value="All" data-id="element-1379" data-mp="132vh1ivjueuz">All Stages</option>
          {PIPELINE_STAGES.map(s => <option key={s} value={s} data-id="element-1380" data-mp="ybwzkfytraq5">
              {s}
            </option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1381" data-mp="1c28sui75bfiv">
        <table className="w-full text-left text-sm" data-id="element-1382" data-mp="17477fqm8z448">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1383" data-mp="173p5qegtfxox">
            <tr data-id="element-1384" data-mp="1m06eqz1j908b">
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1385" data-mp="1m0plrkpna47k">Customer</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1386" data-mp="1h368an0rtvgo">Intent</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1387" data-mp="3ns2p45bys41i">Stage</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1388" data-mp="3nufeh3k1xgol">
                Looking for
              </th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1389" data-mp="1ybpc42rlfox0">
                Last active
              </th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right" data-id="element-1390" data-mp="23atjcnuyjckp">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-1391" data-mp="1ojjjqrxfj5ha">
            {filtered.map((lead, i) => <tr key={i} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedLead(lead)} data-id="element-1392" data-mp="1tinrde4pgv5i" data-mpi={i}>
                <td className="px-4 py-3 font-medium text-dark-navy" data-id="element-1393" data-mp="1jkgdx59mkv89">
                  <div className="flex items-center gap-2" data-id="element-1394" data-mp="15o37qsajkg93">
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-1395" data-mp="1almk1mnoiiye">
                      {lead.name.charAt(0)}
                    </div>
                    {lead.name}
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-1396" data-mp="34xjelohf44xo">
                  <span className={`font-bold ${lead.intentScore >= 80 ? 'text-green-600' : lead.intentScore >= 50 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-1397" data-mp="33jru4vk35dbw">
                    {lead.intentScore}
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-1398" data-mp="2olzfs6q9yuza">
                  <span className={`px-2 py-1 text-xs font-bold rounded-full ${leadStatusBadge(lead.status)}`} data-id="element-1399" data-mp="2on0wortz2box">
                    {lead.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600 truncate max-w-[160px]" data-id="element-1400" data-mp="3cjey9p478x8n">
                  {lead.lookingFor}
                </td>
                <td className="px-4 py-3 text-slate-500" data-id="element-1401" data-mp="37l3kx4ep44lo">
                  {lead.lastActive ?? '—'}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1402" data-mp="8vwhod4lz7k5">
                  <button onClick={e => {
                e.stopPropagation();
                setSelectedLead(lead);
              }} className="text-primary-indigo font-medium hover:underline" data-id="element-1403" data-mp="43v5ssbcriun">
                    View Lead
                  </button>
                </td>
              </tr>)}
            {filtered.length === 0 && <tr data-id="element-1404" data-mp="qd1yoo6ufj2k">
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-1405" data-mp="qdlhe74bibhn">
                  No leads in this stage yet.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={() => setSelectedLead(null)} onStatusChange={updateStatus} onOwnerChange={updateOwner} data-id="element-1406" data-mp="144711y1lipgl" />}
    </div>;
}
function ActivityTab() {
  return <div className="max-w-2xl" data-id="element-1407" data-mp="1j066f0xbmbi1">
      <h3 className="text-lg font-bold text-dark-navy mb-6" data-id="element-1408" data-mp="1j17kle9mve3z">Item Activity</h3>

      <div className="relative border-l-2 border-slate-100 ml-3 space-y-8 pb-4" data-id="element-1409" data-mp="24alatqdgivoh">
        <div className="relative pl-6" data-id="element-1410" data-mp="1zd30bvjsk8ed">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center" data-id="element-1411" data-mp="1zc1kldrtyxqb">
            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-1412" data-mp="28dhmex4kxrix" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1413" data-mp="28beyqjqwgahd">Today, 2:45 PM</div>
          <div className="font-medium text-dark-navy" data-id="element-1414" data-mp="1ko6w69kuvfj1">Price updated</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1415" data-mp="1gn3edhj8z9f6">
            Base price changed from AED 320 to AED 350.
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1416" data-mp="1ufjmgwr09xmy">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-purple-100 border-2 border-white flex items-center justify-center" data-id="element-1417" data-mp="1pm8ayib90sop">
            <div className="w-2 h-2 rounded-full bg-ai-purple" data-id="element-1418" data-mp="11vcpe2xdytpw" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1419" data-mp="wxb6pj3vgwey">Yesterday, 11:20 AM</div>
          <div className="font-medium text-dark-navy" data-id="element-1420" data-mp="38l664zl9vkcz">AI Rule added</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1421" data-mp="3dbwd2h9cjv9s">
            Added rule: "Airport delivery AED 75".
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1422" data-mp="2zlt06gwcxdx0">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-green-100 border-2 border-white flex items-center justify-center" data-id="element-1423" data-mp="2ziwyqilaw73t">
            <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1424" data-mp="3s95l6mmvddo7" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1425" data-mp="5cr6p6lagy1">Oct 12, 09:00 AM</div>
          <div className="font-medium text-dark-navy" data-id="element-1426" data-mp="3a2q9l34vvf">Item published</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1427" data-mp="3iazjo64z69jv">
            Item moved from Draft to Active.
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1428" data-mp="26w2ftq44tfsg">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center" data-id="element-1429" data-mp="26v137h7gwl8m">
            <div className="w-2 h-2 rounded-full bg-slate-400" data-id="element-1430" data-mp="2bssvicc0y9dc" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1431" data-mp="2m5cgcs6o2hmq">Oct 10, 04:15 PM</div>
          <div className="font-medium text-dark-navy" data-id="element-1432" data-mp="2r2kxcpg2wfme">Item created</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1433" data-mp="2d4e9m24ufw0g">
            Imported via Spreadsheet upload.
          </div>
        </div>
      </div>
    </div>;
}
function SettingsTab({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    trialActive,
    startTrial
  } = useBusiness();
  const VALIDITY_TOTAL = 30;
  const [published, setPublished] = useState(true);
  const [hideFromSearch, setHideFromSearch] = useState(false);
  const [archived, setArchived] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(12);
  const [showUnpublish, setShowUnpublish] = useState(false);
  const [unpublishReason, setUnpublishReason] = useState('sold');
  const [otherReason, setOtherReason] = useState('');
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [itemCategory, setItemCategory] = useState('SUVs');
  const [flash, setFlash] = useState<string | null>(null);
  const notify = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 2500);
  };
  const handlePublishToggle = () => {
    if (published) {
      // Reveal the unpublish reason flow rather than unpublishing instantly.
      setShowUnpublish(v => !v);
    } else {
      // Re-publishing requires an active trial.
      if (!trialActive) {
        setShowTrialPrompt(true);
      } else {
        setPublished(true);
        notify('Item published — now visible to customers.');
      }
    }
  };
  const confirmUnpublish = () => {
    setPublished(false);
    setShowUnpublish(false);
    notify('Item unpublished.');
  };
  const extendValidity = () => {
    setDaysRemaining(d => Math.min(VALIDITY_TOTAL, d + 30));
    notify('Validity extended by 30 days.');
  };
  const toggleHide = () => {
    setHideFromSearch(v => !v);
    notify(hideFromSearch ? 'Item is now searchable.' : 'Item hidden from search.');
  };
  const toggleArchive = () => {
    const next = !archived;
    setArchived(next);
    if (next) setPublished(false);
    notify(next ? 'Item archived.' : 'Item restored to active catalog.');
  };
  const validityPct = Math.round(daysRemaining / VALIDITY_TOTAL * 100);
  return <div className="max-w-2xl space-y-8" data-id="element-1434" data-mp="1dyumhwfu7xi2">
      {/* Flash feedback */}
      <AnimatePresence data-id="element-1435" data-mp="1iv1k91y2y2ox">
        {flash && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} className="sticky top-0 z-20 flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 text-green-800 text-sm font-medium rounded-xl shadow-sm" data-id="element-1436" data-mp="1nuxl4sbhde09">
            <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1437" data-mp="2i372spm4nz8" />
            {flash}
          </motion.div>}
      </AnimatePresence>

      <section data-id="element-1438" data-mp="3mcpyxgwydbj8">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1439" data-mp="3mc7rdo4ubaw0">
          Classification
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-id="element-1440" data-mp="3hd4zl5dvf15z">
          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1441" data-mp="3dc1k4jzqj75a">
            Category
          </label>
          <select value={itemCategory} onChange={e => {
          setItemCategory(e.target.value);
          notify(`Category set to ${e.target.value}.`);
        }} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm outline-none" data-id="element-1442" data-mp="dwo6pdi8j0sk">
            <option value="SUVs" data-id="element-1443" data-mp="3w4naiorvi5ih">SUVs</option>
            <option value="Sedans" data-id="element-1444" data-mp="2pmqp7j52g6z2">Sedans</option>
            <option value="Hatchbacks" data-id="element-1445" data-mp="2pke08m0w4wci">Hatchbacks</option>
            <option value="Luxury" data-id="element-1446" data-mp="2kmcf7vjwmmye">Luxury</option>
            <option value="Sports" data-id="element-1447" data-mp="2kpqid60e706w">Sports</option>
          </select>
          <p className="text-xs text-slate-500 mt-2" data-id="element-1448" data-mp="cd4ovvkuh7lg">
            Categorizing your item helps customers find it faster when browsing
            or chatting with AI.
          </p>
        </div>
      </section>

      <section data-id="element-1449" data-mp="3fua408tj0bs">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1450" data-mp="3c6fsyvju9w0">
          Listing Validity
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-id="element-1451" data-mp="7d8glk546u03">
          <div className="flex items-center justify-between mb-2" data-id="element-1452" data-mp="v5yrdq1a778s">
            <div className="font-medium text-dark-navy" data-id="element-1453" data-mp="1037bvx9wjnd7">Days Remaining</div>
            <div className="font-bold text-primary-indigo" data-id="element-1454" data-mp="m4r3m1we5osy">
              {daysRemaining} days
            </div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3" data-id="element-1455" data-mp="r2iuqtp7p6w3">
            <motion.div className="h-full bg-primary-indigo" animate={{
            width: `${validityPct}%`
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 26
          }} data-id="element-1456" data-mp="36ugigex3haln" />
          </div>
          <p className="text-sm text-slate-500" data-id="element-1457" data-mp="2zanrj62uopj3">
            This listing is valid for {VALIDITY_TOTAL} days.
          </p>
          <button onClick={extendValidity} disabled={daysRemaining >= VALIDITY_TOTAL} className="mt-4 px-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1458" data-mp="3d8vt5rau0f0l">
            {daysRemaining >= VALIDITY_TOTAL ? 'Validity at maximum' : 'Extend Validity (+30 days)'}
          </button>
        </div>
      </section>

      <section data-id="element-1459" data-mp="2pjkpo223k6ir">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1460" data-mp="21vjndavihmbs">
          Visibility &amp; Status
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-id="element-1461" data-mp="1wx8dx194pdu3">
          <div className="flex items-center justify-between" data-id="element-1462" data-mp="tlmcdw8jz6v">
            <div data-id="element-1463" data-mp="tbrod08x3uu">
              <div className="font-medium text-dark-navy flex items-center gap-2" data-id="element-1464" data-mp="rr7nhbk53b8">
                Published Status
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md ${published ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-1465" data-mp="ajn7awtal1hk">
                  {published ? 'Live' : 'Unpublished'}
                </span>
              </div>
              <div className="text-sm text-slate-500" data-id="element-1466" data-mp="3iyosm1xxuh8v">
                Make this item visible to customers.
              </div>
            </div>
            <button onClick={handlePublishToggle} title="Toggle published" data-id="element-1467" data-mp="3mzh12syzki43">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${published ? 'text-green-500' : 'text-slate-300 rotate-180'}`} data-id="element-1468" data-mp="3s0oa0vcv7ncc" />
            </button>
          </div>

          {showTrialPrompt && !trialActive && <div className="mt-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl" data-id="element-1469" data-mp="1hdkzgo09f2w1">
              <div className="flex items-start gap-3" data-id="element-1470" data-mp="1hff9h79ztalc">
                <SparklesIcon className="w-5 h-5 text-primary-indigo flex-shrink-0 mt-0.5" data-id="element-1471" data-mp="1cfsxf15wi0e4" />
                <div data-id="element-1472" data-mp="17mq2oppe0kwc">
                  <h4 className="font-bold text-indigo-900 mb-1" data-id="element-1473" data-mp="17m859pqzo0mu">
                    Activate your 7-day free trial
                  </h4>
                  <p className="text-sm text-indigo-800 mb-3" data-id="element-1474" data-mp="ymtjh3lroe8o">
                    Start your free trial to publish inventory and start
                    receiving qualified leads.
                  </p>
                  <button onClick={() => {
                startTrial();
                setShowTrialPrompt(false);
                setPublished(true);
                notify('Trial started — item published.');
              }} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors" data-id="element-1475" data-mp="oongeio85228">
                    Start Free Trial
                  </button>
                </div>
              </div>
            </div>}

          <AnimatePresence initial={false} data-id="element-1476" data-mp="32dsnc7ku5qpl">
            {showUnpublish && published && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1477" data-mp="2ok1fr4qo0gwi">
                <div className="pl-4 border-l-2 border-slate-100 space-y-3" data-id="element-1478" data-mp="25nicux6gfp8e">
                  <label className="block text-sm font-medium text-slate-700" data-id="element-1479" data-mp="2al218pwv9ie2">
                    Reason for unpublishing
                  </label>
                  <select value={unpublishReason} onChange={e => setUnpublishReason(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm" data-id="element-1480" data-mp="8svo0vgakcxr">
                    <option value="sold" data-id="element-1481" data-mp="rpxjwx60pmmp">Sold</option>
                    <option value="not_for_sale" data-id="element-1482" data-mp="rqp4lcy2tzy7">Not for sale anymore</option>
                    <option value="other" data-id="element-1483" data-mp="mxw10secgxnp">Other</option>
                  </select>
                  {unpublishReason === 'other' && <input type="text" value={otherReason} onChange={e => setOtherReason(e.target.value)} placeholder="Please specify..." className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm" data-id="element-1484" data-mp="11pemv6tvxmez" />}
                  <div className="flex gap-2" data-id="element-1485" data-mp="38eb3ao161sza">
                    <button onClick={confirmUnpublish} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1486" data-mp="38fm72num30qe">
                      Confirm Unpublish
                    </button>
                    <button onClick={() => setShowUnpublish(false)} className="px-4 py-2 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-100 transition-colors" data-id="element-1487" data-mp="27kufbqxb28ju">
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          <div className="h-px bg-slate-100" data-id="element-1488" data-mp="1ont5nsst3k9k" />
          <div className="flex items-center justify-between" data-id="element-1489" data-mp="1jozrdy5p3p8c">
            <div data-id="element-1490" data-mp="1jqkpfxr7xqay">
              <div className="font-medium text-dark-navy" data-id="element-1491" data-mp="3j1lowe0umnur">Hide from Search</div>
              <div className="text-sm text-slate-500" data-id="element-1492" data-mp="3izhmy1mifyx4">
                Item can only be accessed via direct link.
              </div>
            </div>
            <button onClick={toggleHide} title="Toggle hide from search" data-id="element-1493" data-mp="3mkb153os7ixo">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${hideFromSearch ? 'text-primary-indigo' : 'text-slate-300 rotate-180'}`} data-id="element-1494" data-mp="3qkv2ykkavaq5" />
            </button>
          </div>
        </div>
      </section>

      <section data-id="element-1495" data-mp="447bpgfg7m79">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1496" data-mp="44qqwpdprwhl">Danger Zone</h3>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-4" data-id="element-1497" data-mp="33lysrk5p2d35">
          <div className="flex items-center justify-between" data-id="element-1498" data-mp="37lh5vtr9xjvf">
            <div data-id="element-1499" data-mp="3clbsvm7xhpb7">
              <div className="font-medium text-red-900" data-id="element-1500" data-mp="3cksnt63dmvel">
                {archived ? 'Restore Item' : 'Archive Item'}
              </div>
              <div className="text-sm text-red-700" data-id="element-1501" data-mp="3hlzvl55irul9">
                {archived ? 'Bring this item back into your active catalog.' : 'Remove from active catalog but keep data.'}
              </div>
            </div>
            <button onClick={toggleArchive} className="px-4 py-2 bg-white border border-red-200 text-red-700 font-medium rounded-lg hover:bg-red-100 flex items-center gap-2 transition-colors" data-id="element-1502" data-mp="75s9ipr4b3oj">
              <ArchiveIcon className="w-4 h-4" data-id="element-1503" data-mp="h36mughqvccm" />{' '}
              {archived ? 'Restore' : 'Archive'}
            </button>
          </div>
          <div className="h-px bg-red-100" data-id="element-1504" data-mp="c32b13rs75sq" />
          <div className="flex items-center justify-between" data-id="element-1505" data-mp="3kk5fvg35yb38">
            <div data-id="element-1506" data-mp="3kim0drpc042i">
              <div className="font-medium text-red-900" data-id="element-1507" data-mp="3fm3swk2m9rmh">Delete Item</div>
              <div className="text-sm text-red-700" data-id="element-1508" data-mp="3fk0v3dv82u77">
                Permanently remove this item and all its data.
              </div>
            </div>
            {confirmDelete ? <div className="flex items-center gap-2" data-id="element-1509" data-mp="3piqc74faoame">
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-1510" data-mp="31tnnxdq5njix">
                  Cancel
                </button>
                <button onClick={onClose} className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors" data-id="element-1511" data-mp="1h41rgejknz0z">
                  <TrashIcon className="w-4 h-4" data-id="element-1512" data-mp="17dpb6yisis0n" /> Confirm Delete
                </button>
              </div> : <button onClick={() => setConfirmDelete(true)} className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors" data-id="element-1513" data-mp="1v54fgwu4njej">
                <TrashIcon className="w-4 h-4" data-id="element-1514" data-mp="1m5hdo9xhkbky" /> Delete
              </button>}
          </div>
        </div>
      </section>
    </div>;
}
interface KnowledgeRule {
  id: number;
  type: string;
  cond: string;
  charge: string;
  active: boolean;
}
interface KnowledgeFaq {
  id: number;
  q: string;
  a: string;
  approved: boolean;
}
function AIRulesTab() {
  const [rules, setRules] = useState<KnowledgeRule[]>([{
    id: 1,
    type: 'Delivery Rule',
    cond: 'Dubai delivery',
    charge: 'AED 50',
    active: true
  }, {
    id: 2,
    type: 'Pickup Rule',
    cond: 'Dubai pickup',
    charge: 'AED 50',
    active: true
  }, {
    id: 3,
    type: 'Airport Rule',
    cond: 'Airport delivery',
    charge: 'AED 75',
    active: true
  }, {
    id: 4,
    type: 'Deposit Rule',
    cond: 'Security deposit',
    charge: 'AED 1,000',
    active: true
  }, {
    id: 5,
    type: 'Restriction',
    cond: 'Minimum rental period',
    charge: '2 days',
    active: false
  }]);
  const [draft, setDraft] = useState('');
  const suggestions = ['Free delivery for rentals over 7 days', 'Add AED 100 for Sharjah delivery', 'No smoking inside the vehicle', 'Weekend rate +15%'];
  // Parse a natural-language rule into a structured row. Pulls out any
  // "AED <amount>" or "<n> day(s)" as the charge, classifies the type by
  // keyword, and uses the full text as the condition.
  const buildRule = (text: string): KnowledgeRule => {
    const trimmed = text.trim();
    const chargeMatch = trimmed.match(/AED\s?[\d,]+/i);
    const durationMatch = trimmed.match(/\d+\s?days?/i);
    const charge = chargeMatch ? chargeMatch[0].toUpperCase().replace('AED', 'AED ').replace(/\s+/g, ' ').trim() : durationMatch ? durationMatch[0] : '—';
    const lower = trimmed.toLowerCase();
    let type = 'Custom Rule';
    if (lower.includes('deliver')) type = 'Delivery Rule';else if (lower.includes('pickup') || lower.includes('pick up')) type = 'Pickup Rule';else if (lower.includes('airport')) type = 'Airport Rule';else if (lower.includes('deposit')) type = 'Deposit Rule';else if (lower.includes('no ') || lower.includes('not ') || lower.includes('minimum') || lower.includes('restrict')) type = 'Restriction';
    return {
      id: Date.now(),
      type,
      cond: trimmed,
      charge,
      active: true
    };
  };
  const addRule = (text?: string) => {
    const value = (text ?? draft).trim();
    if (!value) return;
    setRules(prev => [buildRule(value), ...prev]);
    if (!text) setDraft('');
  };
  const toggleRule = (id: number) => setRules(prev => prev.map(r => r.id === id ? {
    ...r,
    active: !r.active
  } : r));
  const deleteRule = (id: number) => setRules(prev => prev.filter(r => r.id !== id));
  const activeCount = rules.filter(r => r.active).length;
  // ---- FAQs ----
  const [faqs, setFaqs] = useState<KnowledgeFaq[]>([{
    id: 101,
    q: 'What is the minimum rental period?',
    a: 'The minimum rental period for this vehicle is 2 days.',
    approved: true
  }, {
    id: 102,
    q: 'Do you offer delivery to the airport?',
    a: 'Yes, airport delivery is available for an additional charge of AED 75.',
    approved: true
  }, {
    id: 103,
    q: 'Is insurance included in the price?',
    a: 'Basic comprehensive insurance is included. Full coverage is available as an add-on.',
    approved: false
  }, {
    id: 104,
    q: 'What documents are required to rent?',
    a: 'You will need your Emirates ID (or Passport with Visa) and a credit card for the security deposit.',
    approved: true
  }]);
  const [adding, setAdding] = useState(false);
  const [draftQ, setDraftQ] = useState('');
  const [draftA, setDraftA] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQ, setEditQ] = useState('');
  const [editA, setEditA] = useState('');
  const addFaq = () => {
    if (!draftQ.trim() || !draftA.trim()) return;
    setFaqs(prev => [{
      id: Date.now(),
      q: draftQ.trim(),
      a: draftA.trim(),
      approved: true
    }, ...prev]);
    setDraftQ('');
    setDraftA('');
    setAdding(false);
  };
  const deleteFaq = (id: number) => setFaqs(prev => prev.filter(f => f.id !== id));
  const toggleApprove = (id: number) => setFaqs(prev => prev.map(f => f.id === id ? {
    ...f,
    approved: !f.approved
  } : f));
  const startEdit = (f: KnowledgeFaq) => {
    setEditingId(f.id);
    setEditQ(f.q);
    setEditA(f.a);
  };
  const saveEdit = () => {
    setFaqs(prev => prev.map(f => f.id === editingId ? {
      ...f,
      q: editQ.trim(),
      a: editA.trim()
    } : f));
    setEditingId(null);
  };
  const approvedCount = faqs.filter(f => f.approved).length;
  return <div className="flex flex-col h-full max-w-4xl" data-id="element-1515" data-mp="1y7shnwpgzy6v">
      <div className="mb-5" data-id="element-1516" data-mp="doqapfgmdepg">
        <h3 className="text-lg font-bold text-dark-navy mb-2" data-id="element-1517" data-mp="dnexkp1aaulo">AI Knowledge</h3>
        <p className="text-sm text-slate-500" data-id="element-1518" data-mp="hpikc9tellm9">
          Everything Talab needs to answer buyers about this item — pricing
          rules and frequently asked questions, in one place.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-10" data-id="element-1519" data-mp="rohmd9jnyj0a">
        {/* ============ SECTION 1: PRICING & RULES ============ */}
        <section data-id="element-1520" data-mp="3q7oggv60svd7">
          <div className="flex items-center justify-between mb-4" data-id="element-1521" data-mp="3q40oi0g84h0l">
            <div className="flex items-center gap-2.5" data-id="element-1522" data-mp="3v5hlhkvl7jbw">
              <div className="w-8 h-8 rounded-lg bg-ai-purple/10 flex items-center justify-center" data-id="element-1523" data-mp="3y54b5upf5vz">
                <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1524" data-mp="8pvjbp4l6htq" />
              </div>
              <div data-id="element-1525" data-mp="37fnaz9yqq6ut">
                <h4 className="font-bold text-dark-navy leading-tight" data-id="element-1526" data-mp="37g5gyyb7fzfx">
                  Pricing &amp; Rules
                </h4>
                <p className="text-xs text-slate-500" data-id="element-1527" data-mp="3ccvwnkj3kt0t">
                  {activeCount} of {rules.length} rules active
                </p>
              </div>
            </div>
          </div>

          {/* Natural Language Input */}
          <div className="mb-4" data-id="element-1528" data-mp="3m9f1v4cjftag">
            <div className="relative" data-id="element-1529" data-mp="3r6fa9lwv184q">
              <div className="absolute top-3 left-3 text-ai-purple" data-id="element-1530" data-mp="3r5dlkemx8n4j">
                <SparklesIcon className="w-5 h-5" data-id="element-1531" data-mp="1f5z5mvoev0" />
              </div>
              <textarea rows={2} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                addRule();
              }
            }} placeholder='E.g. "If customer wants delivery to Sharjah, add AED 100."' className="w-full pl-10 pr-28 py-3 bg-purple-50/50 border border-purple-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-ai-purple/30 focus:border-ai-purple text-sm resize-none" data-id="element-1532" data-mp="428t3wshxpmu" />
              <button onClick={() => addRule()} disabled={!draft.trim()} className="absolute right-2 bottom-2 px-3 py-1.5 bg-ai-purple text-white text-xs font-bold rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1" data-id="element-1533" data-mp="2cax9dubfo4pz">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-1534" data-mp="2vy3c80vaygbo" /> Add Rule
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-id="element-1535" data-mp="2qy8ilpk57cmx">
              {suggestions.map(s => <button key={s} onClick={() => addRule(s)} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-full hover:border-ai-purple hover:text-ai-purple hover:bg-purple-50/50 transition-colors flex items-center gap-1" data-id="element-1536" data-mp="2m708ssewhp98">
                  <PlusIcon className="w-3 h-3" data-id="element-1537" data-mp="39w1n2waezumf" /> {s}
                </button>)}
            </div>
          </div>

          {/* Structured Rules Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1538" data-mp="1c0co7tjop52q">
            <table className="w-full text-left text-sm" data-id="element-1539" data-mp="yaizdb86ikt0">
              <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1540" data-mp="y7wqn89jxz97">
                <tr data-id="element-1541" data-mp="137stv5afdmnp">
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1542" data-mp="136zmq37k0v51">
                    Rule Type
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1543" data-mp="tawi8qp8ckex">
                    Condition
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1544" data-mp="fbwy9wcnwtp3">
                    Charge
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1545" data-mp="kb15wijxujdb">
                    Status
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600 text-right" data-id="element-1546" data-mp="29n39yhxtdhs2">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1547" data-mp="2oszb94hdv7es">
                <AnimatePresence initial={false} data-id="element-1548" data-mp="2jtcu4jyfp70p">
                  {rules.map(rule => <motion.tr key={rule.id} layout initial={{
                  opacity: 0,
                  backgroundColor: 'rgba(168,85,247,0.06)'
                }} animate={{
                  opacity: 1,
                  backgroundColor: 'rgba(255,255,255,0)'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className={`hover:bg-slate-50 transition-colors group ${rule.active ? '' : 'opacity-60'}`} data-id="element-1549" data-mp="2xk9clhync7u1">
                      <td className="px-4 py-3 font-medium text-dark-navy whitespace-nowrap" data-id="element-1550" data-mp="1m75tfl593ytm">
                        {rule.type}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs" data-id="element-1551" data-mp="1jjv4x5iv7008">
                        {rule.cond}
                      </td>
                      <td className="px-4 py-3 text-slate-900 font-medium whitespace-nowrap" data-id="element-1552" data-mp="1om3wbxg39u08">
                        {rule.charge}
                      </td>
                      <td className="px-4 py-3" data-id="element-1553" data-mp="1xjmodls5h8lm">
                        <button onClick={() => toggleRule(rule.id)} title="Toggle status" className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${rule.active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-id="element-1554" data-mp="1xibj1hj23sse">
                          {rule.active ? <>
                              <CheckCircle2Icon className="w-3 h-3" data-id="element-1555" data-mp="h4le68k450hp" /> Active
                            </> : <>
                              <ClockIcon className="w-3 h-3" data-id="element-1556" data-mp="elhon9bmxk06" /> Paused
                            </>}
                        </button>
                      </td>
                      <td className="px-4 py-3" data-id="element-1557" data-mp="4msddze8uxsw">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1558" data-mp="m8kj80pegck">
                          <button onClick={() => toggleRule(rule.id)} title={rule.active ? 'Pause rule' : 'Activate rule'} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md transition-colors" data-id="element-1559" data-mp="3rst64c7zow27">
                            <ToggleRightIcon className="w-4 h-4" data-id="element-1560" data-mp="3d5gh2diz4872" />
                          </button>
                          <button onClick={() => deleteRule(rule.id)} title="Delete rule" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-1561" data-mp="3918ualw2t2bh">
                            <TrashIcon className="w-4 h-4" data-id="element-1562" data-mp="2yety1oy8f1g9" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>)}
                </AnimatePresence>
                {rules.length === 0 && <tr data-id="element-1563" data-mp="3d75nwwlck1lz">
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-1564" data-mp="3d7xh60rh1n93">
                      No rules yet. Add one above to teach the AI how to handle
                      this item.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </section>

        {/* ============ SECTION 2: FAQs ============ */}
        <section data-id="element-1565" data-mp="4qsy1pffla2i">
          <div className="flex items-center justify-between mb-4" data-id="element-1566" data-mp="4rc345jzg3z4">
            <div className="flex items-center gap-2.5" data-id="element-1567" data-mp="1pfeijbx359e8">
              <div className="w-8 h-8 rounded-lg bg-primary-indigo/10 flex items-center justify-center" data-id="element-1568" data-mp="1pe3j1oaoansx">
                <MessageSquareIcon className="w-4 h-4 text-primary-indigo" data-id="element-1569" data-mp="1y8qg9x66ntu3" />
              </div>
              <div data-id="element-1570" data-mp="1u74xjv6ywlbz">
                <h4 className="font-bold text-dark-navy leading-tight" data-id="element-1571" data-mp="1u6muofpsv8v3">
                  Frequently Asked Questions
                </h4>
                <p className="text-xs text-slate-500" data-id="element-1572" data-mp="16i3vcpoqa6f5">
                  {approvedCount} of {faqs.length} approved for AI replies
                </p>
              </div>
            </div>
            <button onClick={() => setAdding(v => !v)} className="px-3.5 py-2 bg-primary-indigo/10 text-primary-indigo text-sm font-semibold rounded-lg hover:bg-primary-indigo/20 transition-colors flex items-center gap-1.5" data-id="element-1573" data-mp="1fj1r6kp0j7mn">
              <PlusIcon className="w-4 h-4" data-id="element-1574" data-mp="2lyu9rbpnqd7d" /> Add FAQ
            </button>
          </div>

          {/* Add FAQ form */}
          <AnimatePresence initial={false} data-id="element-1575" data-mp="361i3ug61jy9h">
            {adding && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1576" data-mp="3aza3jwcxgoo8">
                <div className="p-4 mb-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3" data-id="element-1577" data-mp="3tpi3y6p8t10q">
                  <input type="text" value={draftQ} onChange={e => setDraftQ(e.target.value)} placeholder="Question — e.g. Can I extend my rental?" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-1578" data-mp="2j6ylyd79e3l" />
                  <textarea rows={2} value={draftA} onChange={e => setDraftA(e.target.value)} placeholder="Answer the AI should give…" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-1579" data-mp="20v8qix4lqjzy" />
                  <div className="flex justify-end gap-2" data-id="element-1580" data-mp="2esove47925pl">
                    <button onClick={() => {
                  setAdding(false);
                  setDraftQ('');
                  setDraftA('');
                }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg transition-colors" data-id="element-1581" data-mp="2er402cd42iqo">
                      Cancel
                    </button>
                    <button onClick={addFaq} disabled={!draftQ.trim() || !draftA.trim()} className="px-4 py-2 bg-primary-indigo text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-1582" data-mp="3nt2eyt7xyks2">
                      Save FAQ
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* FAQ list */}
          <div className="space-y-3" data-id="element-1583" data-mp="6cc75qo6m1me">
            <AnimatePresence initial={false} data-id="element-1584" data-mp="kcmz6x189m95">
              {faqs.map(faq => <motion.div key={faq.id} layout initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              height: 0
            }} className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition-all group" data-id="element-1585" data-mp="kaivog78qp9e">
                  {editingId === faq.id ? <div className="space-y-3" data-id="element-1586" data-mp="nsq5tsltybwh">
                      <input type="text" value={editQ} onChange={e => setEditQ(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1587" data-mp="rv31fo6v4tfj" />
                      <textarea rows={2} value={editA} onChange={e => setEditA(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1588" data-mp="8ybnlua3b6iv" />
                      <div className="flex justify-end gap-2" data-id="element-1589" data-mp="378dk3etl3r4h">
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg transition-colors" data-id="element-1590" data-mp="337a1wlo2dj03">
                          Cancel
                        </button>
                        <button onClick={saveEdit} className="px-3 py-1.5 bg-primary-indigo text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5" data-id="element-1591" data-mp="3qww4zk7k4idd">
                          <CheckIcon className="w-3.5 h-3.5" data-id="element-1592" data-mp="ih2oh5d876wb" /> Save
                        </button>
                      </div>
                    </div> : <div className="flex items-start justify-between gap-4" data-id="element-1593" data-mp="dorknrfd70yh">
                      <div className="flex-1 min-w-0" data-id="element-1594" data-mp="l3ldw06t3puo">
                        <div className="flex items-center gap-2 mb-2" data-id="element-1595" data-mp="q023yzxkgeae">
                          <h4 className="font-bold text-dark-navy flex items-center gap-2" data-id="element-1596" data-mp="uzy9x4ptqfsl">
                            <span className="text-slate-400" data-id="element-1597" data-mp="v2aw5u2m7cbg">Q:</span> {faq.q}
                          </h4>
                          {!faq.approved && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold uppercase tracking-wide rounded-md" data-id="element-1598" data-mp="13ztpdlqewwyf">
                              Draft
                            </span>}
                        </div>
                        <p className="text-sm text-slate-600 flex items-start gap-2" data-id="element-1599" data-mp="1iq0w54724rja">
                          <span className="text-ai-purple font-bold mt-0.5" data-id="element-1600" data-mp="1mre5ho9czcuy">
                            A:
                          </span>{' '}
                          {faq.a}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1601" data-mp="1e25s1yne2miy">
                        <button onClick={() => toggleApprove(faq.id)} title={faq.approved ? 'Approved — click to unapprove' : 'Approve for AI'} className={`p-1.5 rounded-md transition-colors ${faq.approved ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`} data-id="element-1602" data-mp="1a2olf1w87jc7">
                          <CheckCircle2Icon className="w-4 h-4" data-id="element-1603" data-mp="154d82h6q2qp8" />
                        </button>
                        <button onClick={() => startEdit(faq)} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md transition-colors" title="Edit" data-id="element-1604" data-mp="1024i7tp5c4rc">
                          <EditIcon className="w-4 h-4" data-id="element-1605" data-mp="3tfpsipl04l2i" />
                        </button>
                        <button onClick={() => deleteFaq(faq.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete" data-id="element-1606" data-mp="2b999yvyuwb1">
                          <TrashIcon className="w-4 h-4" data-id="element-1607" data-mp="gyu7bedzi5c3" />
                        </button>
                      </div>
                    </div>}
                </motion.div>)}
            </AnimatePresence>
            {faqs.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl" data-id="element-1608" data-mp="c8du9ve2slq9">
                No FAQs yet. Add one so the AI can answer common questions
                instantly.
              </div>}
          </div>
        </section>
      </div>
    </div>;
}